import {
  isShareTargetPickerAvailable,
  openShareTargetPicker,
  sendInviteURL,
} from 'src/lib/sharing';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { makePayload } from 'src/lib/analytics';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import { AnalyticsProperties } from '@play-co/gcinstant';
import i18n from 'src/lib/i18n/i18n';
import { getCreativeText } from 'src/creatives/text';
import { track } from 'src/lib/analytics/utils';
import { getRandomAddFriendsV2Asset } from 'src/replicant/chatbot/flex/invite';
import {
  createFlexContainer,
  createOpenGameAction,
  FlexCreativeAsset,
} from 'src/replicant/chatbot/flex/utils';
import { AlternativeOpenWebBuckets } from 'src/replicant/utils/payload';
import { getBuruFriendsCount } from 'src/replicant/friends/friends.getters';

const ADD_FRIEND_FEATURE = 'add_friend';

type ShareAddFriendInviteOptions = {
  forceNativeShare?: boolean;
  originFeature?: typeof Feature.PET_DIALOG._;
};

function getBasePayload(
  feature: typeof Feature.FRIENDS._ | typeof Feature.PET_DIALOG._,
  subFeature: string,
) {
  return {
    ...makePayload('SHARE'),
    channel: 'SHARE' as AnalyticsProperties.Channel,
    feature,
    subFeature,
    $subFeature: subFeature,
    originFeature: feature,
    originSubFeature: subFeature,
    webReferrer: user().id,
  };
}

/**
 * Share add friend invite via LINE Share Target Picker (preferred) or native share (fallback).
 * This function prioritizes LINE Share Target Picker when available, falling back to native URL share.
 *
 * @param inviteUrl - The invite URL to share
 * @param opts - Optional configuration
 * @param opts.forceNativeShare - If true, skip Share Target Picker and use native share directly
 */
export async function shareAddFriendInvite(
  inviteUrl: string,
  opts?: ShareAddFriendInviteOptions,
): Promise<void> {
  const isPetDialog = opts?.originFeature === Feature.PET_DIALOG._;

  // Prioritize LINE Share Target Picker when available (unless forceNativeShare is set)
  if (!opts?.forceNativeShare && isShareTargetPickerAvailable()) {
    await sendAddFriendShareTargetPicker(isPetDialog);

    // Track FriendRequestSend on share via LINE Share Target Picker
    track('FriendRequestSend', {
      method: 'line_share',
      friendCount: getBuruFriendsCount(user()),
      ...(isPetDialog
        ? {
            originFeature: Feature.PET_DIALOG._,
            originSubFeature: Feature.PET_DIALOG.LINE_SHARE,
          }
        : {
            originFeature: Feature.FRIENDS._,
            originSubFeature: Feature.FRIENDS.LINE_SHARE,
          }),
    });
    return;
  }

  // Fallback to native URL share
  const shareMethod = await sendInviteURL({
    inviteUrl,
    description: i18n('friends.addFriendPopup.shareDescription'),
  });

  // Track SendInviteURL with feature
  track('SendInviteURL', {
    url: inviteUrl,
    shareMethod,
    feature: ADD_FRIEND_FEATURE,
  });

  // Track FriendRequestSend
  track('FriendRequestSend', {
    method: 'native_share',
    friendCount: getBuruFriendsCount(user()),
    ...(isPetDialog
      ? {
          originFeature: Feature.PET_DIALOG._,
          originSubFeature: Feature.PET_DIALOG.NATIVE_SHARE,
        }
      : {
          originFeature: Feature.FRIENDS._,
          originSubFeature: Feature.FRIENDS.NATIVE_SHARE,
        }),
  });
}

/**
 * Share add friend invite via LINE Share Target Picker.
 */
async function sendAddFriendShareTargetPicker(
  isPetDialog: boolean,
): Promise<void> {
  const creativeAsset = getRandomAddFriendsV2Creative();
  const creativeText = getCreativeText('invite');
  const title = process.env.LINE_APP_NAME || 'ぶるぶるどーぶつ';

  const { encodedPayload, analyticsEventProperties } =
    platform.generateEntryPayload({
      ...getBasePayload(
        isPetDialog ? Feature.PET_DIALOG._ : Feature.FRIENDS._,
        isPetDialog
          ? Feature.PET_DIALOG.LINE_SHARE
          : Feature.FRIENDS.LINE_SHARE,
      ),
      $creativeAssetID: creativeAsset.id,
      $creativeTextID: creativeText.id,
      $creativeCTA: creativeText.cta,
      creativeCTA: creativeText.cta,
    });

  const flexMessage = createAddFriendsV2FlexMessage({
    title,
    asset: creativeAsset,
    cta: creativeText.cta,
    payload: {
      ...encodedPayload,
      alternativeWebIABBucket: 'enabled_mini_app' as const,
    },
  });

  await openShareTargetPicker(flexMessage, analyticsEventProperties);
}

/**
 * Create Add Friends V2 flex message with hero image only.
 * Matches the daily fortune share template pattern.
 */
function createAddFriendsV2FlexMessage(args: {
  title: string;
  asset: FlexCreativeAsset;
  cta: string;
  payload: object & { alternativeWebIABBucket: AlternativeOpenWebBuckets };
}) {
  const { title, asset, cta, payload } = args;
  return createFlexContainer(title, {
    type: 'bubble',
    hero: {
      type: 'image',
      url: asset.image,
      size: 'full',
      aspectRatio: '73:41', // 730:410 creative dimensions
      aspectMode: 'cover',
      action: createOpenGameAction(cta, payload),
    },
  });
}

/**
 * Get random Add Friends V2 creative asset for share target picker.
 */
function getRandomAddFriendsV2Creative(): FlexCreativeAsset {
  const creative = getRandomAddFriendsV2Asset(Math.random);
  creative.image = StateObserver.replicant.getChatbotAssetUrl(creative.image);
  return creative;
}
